import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { ArrowLeft, Music, Info, Search, Settings, Lock, Crown, Zap, ChevronDown, Target, X, Trash2, Grid3X3 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Import the real engine
import { applyAtFret, chordFromStrings, generateGrips } from '../engine';

// Admin emails that get Pro features (for testing in preview)
const ADMIN_PRO_EMAILS = ['george@streaklabs.com', 'admin@streaklabs.com'];

// Check if forcePro is enabled (preview mode only)
const isForcePro = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('forcePro') === '1';
};

const ROOT_NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Chord family definitions with STRICT patterns for filtering
const CHORD_FAMILIES = [
  { value: 'major', label: 'Major', patterns: ['', 'maj', 'M', '6', 'maj7', 'maj9', 'maj6', 'add9', 'add2', '6/9'] },
  { value: 'minor', label: 'Minor', patterns: ['m', 'min', '-'] },
  { value: 'dom7', label: 'Dominant 7', patterns: ['7', '9', '11', '13'] },
  { value: 'maj7', label: 'Major 7', patterns: ['maj7', 'M7', 'Δ7', 'maj9', 'maj13'] },
  { value: 'min7', label: 'Minor 7', patterns: ['m7', 'min7', '-7', 'm9', 'm11', 'm13', 'm6'] },
  { value: 'dim', label: 'Diminished', patterns: ['dim', 'dim7', 'o', 'o7', 'mb5', 'm7b5', 'ø'] },
  { value: 'aug', label: 'Augmented', patterns: ['aug', '+', '#5'] },
  { value: 'sus', label: 'Suspended', patterns: ['sus', 'sus2', 'sus4', '7sus4', '7sus2'] },
];

// Priority order for chord qualities within each family (for steel-friendly sorting)
const CHORD_QUALITY_PRIORITY = {
  major: ['', '6', 'maj7', 'add9', 'maj9', '6/9', 'add2', 'maj6'],
  minor: ['m', 'm7', 'm6', 'm9', 'min', '-'],
  dom7: ['7', '9', '13', '11'],
  maj7: ['maj7', 'M7', 'maj9', 'maj13'],
  min7: ['m7', 'm9', 'm11', 'm13', 'm6'],
  dim: ['dim', 'dim7', 'o', 'o7', 'm7b5'],
  aug: ['aug', '+', '#5'],
  sus: ['sus4', 'sus2', 'sus', '7sus4', '7sus2'],
};

const LEVER_PATTERNS = ['LKL', 'LKR', 'RKL', 'RKR', 'LKV', 'RKV', 'LK', 'RK'];

const isLeverName = (name) => {
  const upperName = (name || '').toUpperCase().trim();
  return LEVER_PATTERNS.some(pattern => upperName.startsWith(pattern));
};

const inferControlType = (ctrl) => {
  if (ctrl.control_type && ctrl.control_type !== '') {
    return ctrl.control_type;
  }
  return isLeverName(ctrl.name) ? 'lever' : 'pedal';
};

function convertToEngineCopedant(backendCopedant) {
  return {
    id: backendCopedant.copedant_id || backendCopedant.id || 'unknown',
    name: backendCopedant.name,
    openTuning: backendCopedant.strings.map((s) => {
      const note = s.open_note;
      if (/\d/.test(note)) return note;
      const stringNum = s.string_number;
      const totalStrings = backendCopedant.strings.length;
      const octave = Math.floor(5 - (stringNum / totalStrings) * 3);
      return `${note}${octave}`;
    }),
    controls: backendCopedant.controls.map((ctrl) => ({
      id: ctrl.name,
      label: ctrl.name,
      type: inferControlType(ctrl),
      changes: ctrl.changes.map((ch) => ({
        string: ch.string_number - 1,
        delta: ch.semitone_change,
      })),
    })),
  };
}

// STRICT chord family normalization - no cross-family leakage
function normalizeChordFamily(chordName) {
  if (!chordName) return { root: null, family: null, quality: null };
  
  const rootMatch = chordName.match(/^([A-G][#b]?)/);
  if (!rootMatch) return { root: null, family: null, quality: null };
  
  const root = rootMatch[1].replace('b', '#');
  let quality = chordName.substring(root.length);
  
  // Handle slash chords - extract base quality before slash
  const slashIndex = quality.indexOf('/');
  const baseQuality = slashIndex > 0 ? quality.substring(0, slashIndex) : quality;
  
  // Check for augmented FIRST (must check before major since aug contains no 'm')
  if (baseQuality.includes('aug') || baseQuality === '+' || baseQuality.includes('#5')) {
    return { root, family: 'aug', quality: baseQuality };
  }
  
  // Check for diminished
  if (baseQuality.includes('dim') || baseQuality.includes('o7') || baseQuality === 'o' || 
      baseQuality.includes('m7b5') || baseQuality.includes('ø')) {
    return { root, family: 'dim', quality: baseQuality };
  }
  
  // Check for suspended
  if (baseQuality.includes('sus')) {
    return { root, family: 'sus', quality: baseQuality };
  }
  
  // Check for minor (must check before major)
  if (baseQuality.startsWith('m') && !baseQuality.startsWith('maj')) {
    // Minor 7 family
    if (baseQuality.includes('7') || baseQuality.includes('9') || baseQuality.includes('11') || baseQuality.includes('13')) {
      return { root, family: 'min7', quality: baseQuality };
    }
    return { root, family: 'minor', quality: baseQuality };
  }
  
  // Check for dominant 7 (has 7 but no 'maj')
  if ((baseQuality.includes('7') || baseQuality.includes('9') || baseQuality.includes('11') || baseQuality.includes('13')) 
      && !baseQuality.includes('maj') && !baseQuality.includes('M7')) {
    return { root, family: 'dom7', quality: baseQuality };
  }
  
  // Check for major 7
  if (baseQuality.includes('maj7') || baseQuality.includes('M7') || baseQuality.includes('Δ') || 
      baseQuality.includes('maj9') || baseQuality.includes('maj13')) {
    return { root, family: 'maj7', quality: baseQuality };
  }
  
  // Default to major (includes: '', '6', 'add9', 'add2', '6/9', 'maj')
  return { root, family: 'major', quality: baseQuality };
}

// Get chord quality priority for sorting within a family
function getChordQualityPriority(quality, family) {
  const priorities = CHORD_QUALITY_PRIORITY[family] || [];
  const index = priorities.findIndex(p => quality === p || quality.startsWith(p));
  return index >= 0 ? index : priorities.length;
}

// Generate unique ID for notes
let noteIdCounter = 0;
const generateNoteId = () => `note_${++noteIdCounter}`;

// ========================================
// CHORD NAMING: Reduce slash chord overuse
// ========================================
// Format chord name to be player-friendly (less slash-heavy)
function formatChordDisplay(chordInfo) {
  if (!chordInfo || !chordInfo.symbol) {
    return { displayName: '—', details: null };
  }
  
  const { symbol, root, quality, bassNote } = chordInfo;
  
  // If no bass note or bass equals root, just show simple name
  if (!bassNote || bassNote === root) {
    return { 
      displayName: symbol, 
      details: null 
    };
  }
  
  // Check if bass note is a common inversion (3rd or 5th of chord)
  // For these cases, show the simple chord name without slash
  const isCommonInversion = isStandardInversion(root, quality, bassNote);
  
  if (isCommonInversion) {
    // Show simple chord name, put bass info in details
    const simpleSymbol = root + (quality || '');
    return {
      displayName: simpleSymbol,
      details: { bass: bassNote, inversion: getInversionName(root, quality, bassNote) }
    };
  }
  
  // For non-standard bass notes, show slash chord
  return {
    displayName: symbol,
    details: { bass: bassNote }
  };
}

// Check if bass note is a standard inversion (3rd or 5th)
function isStandardInversion(root, quality, bassNote) {
  if (!root || !bassNote) return false;
  
  const rootIndex = ROOT_NOTES.indexOf(root.replace('b', '#'));
  if (rootIndex < 0) return false;
  
  const bassIndex = ROOT_NOTES.indexOf(bassNote.replace('b', '#'));
  if (bassIndex < 0) return false;
  
  // Calculate interval from root to bass
  const interval = (bassIndex - rootIndex + 12) % 12;
  
  // Common inversions:
  // Major: 3rd = 4 semitones, 5th = 7 semitones
  // Minor: b3 = 3 semitones, 5th = 7 semitones
  const isMinor = quality && (quality.startsWith('m') && !quality.startsWith('maj'));
  
  if (isMinor) {
    // Minor chord: b3 (3 semitones) or 5th (7 semitones)
    return interval === 3 || interval === 7;
  } else {
    // Major/Dom chord: 3rd (4 semitones) or 5th (7 semitones)
    return interval === 4 || interval === 7;
  }
}

// Get inversion name
function getInversionName(root, quality, bassNote) {
  if (!root || !bassNote) return null;
  
  const rootIndex = ROOT_NOTES.indexOf(root.replace('b', '#'));
  const bassIndex = ROOT_NOTES.indexOf(bassNote.replace('b', '#'));
  if (rootIndex < 0 || bassIndex < 0) return null;
  
  const interval = (bassIndex - rootIndex + 12) % 12;
  const isMinor = quality && (quality.startsWith('m') && !quality.startsWith('maj'));
  
  // 3rd in bass = 1st inversion
  if ((isMinor && interval === 3) || (!isMinor && interval === 4)) {
    return '1st inversion';
  }
  // 5th in bass = 2nd inversion
  if (interval === 7) {
    return '2nd inversion';
  }
  return null;
}

// ========================================
// PLAYABILITY SCORING: Human easiest ranking
// ========================================
function calculatePlayabilityScore(grip) {
  const strings = grip.strings || [];
  const controls = grip.activeControls || [];
  
  if (strings.length === 0) return 999;
  
  const indices = strings.map(s => s.stringIndex).sort((a, b) => a - b);
  const noteCount = strings.length;
  
  // 1. String span penalty (smaller span = easier)
  const span = indices[indices.length - 1] - indices[0];
  const spanPenalty = span * 1.5;
  
  // 2. Skip penalty (gaps between consecutive selected strings)
  let skips = 0;
  for (let i = 1; i < indices.length; i++) {
    skips += (indices[i] - indices[i - 1] - 1);
  }
  const skipPenalty = skips * 3.0;
  
  // 3. Note count penalty (3 notes optimal, 4 OK, 5+ harder)
  let noteCountPenalty = 0;
  if (noteCount >= 5) noteCountPenalty = 2.5;
  else if (noteCount === 4) noteCountPenalty = 1.0;
  
  // 4. Controls complexity penalty (0-2 fine, more adds penalty)
  const controlsPenalty = controls.length > 2 ? (controls.length - 2) * 1.5 : 0;
  
  // 5. Consecutive strings bonus (no skips)
  const isConsecutiveNoSkips = skips === 0;
  const consecutiveBonus = isConsecutiveNoSkips ? 2.0 : 0;
  
  // 6. Strummable block bonus (consecutive + same fret or slant within 1)
  const frets = strings.map(s => s.fret !== undefined ? s.fret : grip.fret);
  const uniqueFrets = [...new Set(frets)];
  const fretSpread = uniqueFrets.length > 0 ? Math.max(...uniqueFrets) - Math.min(...uniqueFrets) : 0;
  const isStrummableBlock = isConsecutiveNoSkips && fretSpread <= 1;
  const strummableBonus = isStrummableBlock ? 2.0 : 0;
  
  // Calculate final score (lower = easier)
  const score = spanPenalty + skipPenalty + noteCountPenalty + controlsPenalty - consecutiveBonus - strummableBonus;
  
  return Math.max(0, score);
}

// Get difficulty label from score
function getDifficultyLabel(score) {
  if (score <= 3) return { label: 'Easy', color: 'text-green-400' };
  if (score <= 7) return { label: 'Medium', color: 'text-yellow-400' };
  return { label: 'Hard', color: 'text-red-400' };
}

// Get difficulty tooltip explaining why
function getDifficultyReason(grip) {
  const strings = grip.strings || [];
  const controls = grip.activeControls || [];
  const indices = strings.map(s => s.stringIndex).sort((a, b) => a - b);
  
  const reasons = [];
  
  // Check for skips
  let skips = 0;
  for (let i = 1; i < indices.length; i++) {
    skips += (indices[i] - indices[i - 1] - 1);
  }
  
  if (skips === 0) reasons.push('consecutive strings');
  else reasons.push(`${skips} skip${skips > 1 ? 's' : ''}`);
  
  if (controls.length === 0) reasons.push('open position');
  else if (controls.length <= 2) reasons.push(`${controls.length} control${controls.length > 1 ? 's' : ''}`);
  else reasons.push(`${controls.length} controls (complex)`);
  
  reasons.push(`${strings.length} notes`);
  
  return reasons.join(', ');
}

// ========================================
// MUSICAL RELEVANCE SCORING
// ========================================

// Common chord families that beginners should see first
const COMMON_CHORD_FAMILIES = new Set([
  'maj', '', 'm', 'min', '7', 'maj7', 'm7', 'min7', 
  'sus2', 'sus4', 'add9', '6', '5', 'dim', 'aug',
  '9', 'add2', 'm6', '6/9'
]);

// Very common beginner-friendly chords (highest priority)
const BEGINNER_CHORD_QUALITIES = new Set([
  'maj', '', 'm', 'min', '7', 'maj7', 'm7', 'sus4', 'sus2', '5'
]);

/**
 * Calculate musical relevance score for chord ranking.
 * Higher score = more musically useful for beginners = shown first.
 * 
 * @param grip The chord grip object
 * @param selectedRoot The user-selected root filter ('any' or note name)
 * @param includeExtensions Whether extensions are enabled
 * @returns Object with score and label
 */
function calculateMusicalRelevanceScore(grip, selectedRoot, includeExtensions) {
  if (!grip.chord?.symbol) return { score: 0, label: null };
  
  const chord = grip.chord;
  const symbol = chord.symbol || '';
  const quality = chord.quality || '';
  const root = chord.root || '';
  const bassNote = chord.bassNote || null;
  
  let score = 0;
  
  // ===== ADD POINTS =====
  
  // +50: Root note matches selected root exactly
  if (selectedRoot !== 'any') {
    const normalizedSelectedRoot = selectedRoot.replace('b', '#');
    const normalizedChordRoot = root.replace('b', '#');
    if (normalizedChordRoot === normalizedSelectedRoot) {
      score += 50;
    }
  }
  
  // +40: Contains full triad (check for presence of 3rd and 5th in quality)
  // Full triads: major (0,4,7), minor (0,3,7) - NOT no3, no5 variants
  const hasThird = !quality.includes('no3') && !quality.includes('sus') && quality !== '5';
  const hasFifth = !quality.includes('no5');
  if (hasThird && hasFifth) {
    score += 40;
  }
  
  // +30: Common chord family
  const qualityLower = quality.toLowerCase();
  if (COMMON_CHORD_FAMILIES.has(quality) || COMMON_CHORD_FAMILIES.has(qualityLower)) {
    score += 30;
  }
  
  // +35: Extra bonus for very beginner-friendly chords
  if (BEGINNER_CHORD_QUALITIES.has(quality) || BEGINNER_CHORD_QUALITIES.has(qualityLower)) {
    score += 35;
  }
  
  // +20: Root position (no slash chord OR bass = root)
  const isRootPosition = !bassNote || bassNote === root;
  if (isRootPosition) {
    score += 20;
  }
  
  // +15: Slash chord with chord tone bass (common inversions are OK)
  if (bassNote && bassNote !== root) {
    // This is a slash chord - still useful but less than root position
    score += 15;
  }
  
  // +10: Extension chords (9, 11, 13) if Include Extensions is enabled
  const isExtension = /[9]|11|13/.test(quality) && !quality.includes('add');
  if (isExtension && includeExtensions) {
    score += 10;
  }
  
  // +8: Pedal steel friendly voicing (≤ 4 notes)
  if (grip.strings && grip.strings.length <= 4) {
    score += 8;
  }
  
  // +5: Lower fret positions (0–8)
  if (grip.fret >= 0 && grip.fret <= 8) {
    score += 5;
  }
  
  // ===== SUBTRACT POINTS =====
  
  // -40: Missing third (no3) - MAJOR PENALTY
  if (quality.includes('no3')) {
    score -= 40;
  }
  
  // -25: Missing fifth (no5)
  if (quality.includes('no5')) {
    score -= 25;
  }
  
  // -20: Altered / rare theory chords
  const isAltered = /b5|#5|b9|#9|#11|b13|alt/.test(quality);
  if (isAltered) {
    score -= 20;
  }
  
  // -15: Complex extensions when simpler match might exist
  const isComplexExtension = /mMaj|aug7|dim7|m7b5/.test(quality);
  if (isComplexExtension) {
    score -= 15;
  }
  
  // -10: High fret positions (>12)
  if (grip.fret > 12) {
    score -= 10;
  }
  
  // -10: Duplicate pitch classes (check for redundant notes)
  if (grip.strings) {
    const pitchClasses = grip.strings.map(s => s.noteName?.replace(/\d+$/, ''));
    const uniquePitches = new Set(pitchClasses);
    if (uniquePitches.size < pitchClasses.length) {
      score -= 10;
    }
  }
  
  // Clamp minimum score at 0
  score = Math.max(0, score);
  
  return { score, quality, isRootPosition, isExtension, hasThird, hasFifth };
}

/**
 * Get visual label for chord result based on score and properties.
 */
function getChordLabel(grip, scoreInfo) {
  const { score, quality, isRootPosition, isExtension, hasThird, hasFifth } = scoreInfo;
  const bassNote = grip.chord?.bassNote;
  const root = grip.chord?.root;
  
  // Check for "no3" or "no5" variants - mark as Advanced
  if (quality && (quality.includes('no3') || quality.includes('no5'))) {
    return { text: 'Advanced', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' };
  }
  
  // High score = Most Common
  if (score >= 80) {
    return { text: 'Common', color: 'bg-green-500/20 text-green-400 border-green-500/30' };
  }
  
  // Slash chord (inversion)
  if (bassNote && bassNote !== root) {
    return { text: 'Inversion', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
  }
  
  // Extension chord
  if (isExtension) {
    return { text: 'Extension', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' };
  }
  
  // Default - no special label for mid-range chords
  return null;
}

export const ExplorerPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const copedantId = searchParams.get('copedantId');

  const [allCopedants, setAllCopedants] = useState([]);
  const [backendCopedant, setBackendCopedant] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Global fret position (used when NOT in slant mode)
  const [globalFret, setGlobalFret] = useState(0);
  const [activeControls, setActiveControls] = useState([]);
  
  // Selected notes: store only position data, NOT note names
  // { id, stringIndex, isOpen, fret }
  const [selectedNotes, setSelectedNotes] = useState([]);
  
  // Slant mode - allows per-string fret positions
  const [slantMode, setSlantMode] = useState(false);
  
  // Fret ruler view
  const [showExtendedFrets, setShowExtendedFrets] = useState(false);
  const maxVisibleFret = showExtendedFrets ? 24 : 12;
  
  // Chord Finder state
  const [chordFinderRoot, setChordFinderRoot] = useState('any');
  const [chordFinderFamily, setChordFinderFamily] = useState('any');
  const [chordFinderFretMin, setChordFinderFretMin] = useState(0);
  const [chordFinderFretMax, setChordFinderFretMax] = useState(12);
  const [chordFinderStringsMin, setChordFinderStringsMin] = useState(3);
  const [chordFinderStringsMax, setChordFinderStringsMax] = useState(6);
  const [includeExtensions, setIncludeExtensions] = useState(true);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showChordFinder, setShowChordFinder] = useState(false);
  
  // Grips state
  const [grips, setGrips] = useState([]);
  const [searchingGrips, setSearchingGrips] = useState(false);

  // Pro status: real Pro OR forcePro query param OR admin email
  const isAdminEmail = user?.email && ADMIN_PRO_EMAILS.some(email => 
    user.email.toLowerCase() === email.toLowerCase()
  );
  const isPro = user?.is_pro || isForcePro() || isAdminEmail;
  const FREE_GRIP_LIMIT = 10;

  // Convert backend copedant to engine format
  const engineCopedant = useMemo(() => {
    if (!backendCopedant) return null;
    try {
      return convertToEngineCopedant(backendCopedant);
    } catch (e) {
      console.error('Error converting copedant:', e);
      return null;
    }
  }, [backendCopedant]);

  // Group controls by type
  const groupedControls = useMemo(() => {
    if (!engineCopedant) return { pedals: [], levers: [] };
    const pedals = engineCopedant.controls.filter(c => c.type === 'pedal');
    const levers = engineCopedant.controls.filter(c => c.type === 'lever');
    return { pedals, levers };
  }, [engineCopedant]);

  // Fetch all copedants
  useEffect(() => {
    fetchAllCopedants();
  }, []);

  // Fetch specific copedant when ID changes
  useEffect(() => {
    if (copedantId) {
      fetchCopedant(copedantId);
    } else {
      setLoading(false);
    }
  }, [copedantId]);

  const fetchAllCopedants = async () => {
    try {
      const response = await api.get(`/copedants`);
      setAllCopedants(response.data);
    } catch (error) {
      console.error('Error fetching copedants:', error);
    }
  };

  const fetchCopedant = async (id) => {
    setLoading(true);
    try {
      const response = await api.get(`/copedants/${id}`);
      setBackendCopedant(response.data);
      setSelectedNotes([]);
      setGrips([]);
      setActiveControls([]);
      setGlobalFret(0);
    } catch (error) {
      console.error('Error fetching copedant:', error);
      toast.error('Failed to load copedant');
      setBackendCopedant(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCopedant = (id) => {
    setSearchParams({ copedantId: id });
  };

  // Toggle control - persists until manually changed
  const toggleControl = useCallback((controlId) => {
    setActiveControls(prev =>
      prev.includes(controlId)
        ? prev.filter(c => c !== controlId)
        : [...prev, controlId]
    );
    // Note: chord will automatically recalculate via useMemo dependency
  }, []);

  // Get note at a specific position (dynamically computed)
  const getNoteAtPosition = useCallback((stringIndex, fretPos, controls) => {
    if (!engineCopedant) return null;
    const result = applyAtFret(engineCopedant, fretPos, controls);
    return result?.strings[stringIndex] || null;
  }, [engineCopedant]);

  // Compute the actual notes for all selected positions (LIVE computation)
  const computedSelectedNotes = useMemo(() => {
    if (!engineCopedant || selectedNotes.length === 0) return [];
    
    return selectedNotes.map(note => {
      const fretToUse = note.isOpen ? 0 : note.fret;
      const stringState = getNoteAtPosition(note.stringIndex, fretToUse, activeControls);
      return {
        ...note,
        noteName: stringState?.noteName || '?',
        midiPitch: stringState?.midiPitch || 0,
        activeControlsOnString: stringState?.activeControls || []
      };
    });
  }, [engineCopedant, selectedNotes, activeControls, getNoteAtPosition]);

  // Real-time chord detection from computed notes
  const chordInfo = useMemo(() => {
    if (computedSelectedNotes.length < 2) return null;
    if (!engineCopedant) return null;
    
    // Get string indices for chord analysis
    const stringIndices = computedSelectedNotes.map(n => n.stringIndex);
    
    // We need to call applyAtFret to get the full result for chordFromStrings
    // Use the first selected note's fret as reference (or 0 for all open)
    const referenceFret = computedSelectedNotes.some(n => !n.isOpen) 
      ? computedSelectedNotes.find(n => !n.isOpen)?.fret || globalFret 
      : 0;
    
    const result = applyAtFret(engineCopedant, referenceFret, activeControls);
    
    try {
      return chordFromStrings(result, stringIndices);
    } catch (e) {
      console.error('Chord detection error:', e);
      return null;
    }
  }, [computedSelectedNotes, engineCopedant, activeControls, globalFret]);

  // Toggle fretted note selection - NO prerequisite fret selection needed
  // Clicking any note pill directly selects it and updates activeFret as UI convenience
  const toggleFrettedNote = useCallback((stringIndex, clickedFret) => {
    // Always use the clicked fret directly - no "select fret first" requirement
    const fretToUse = clickedFret;
    
    // Update globalFret as UI convenience (shows which fret is "active" in ruler)
    // But this is NOT a prerequisite for selection
    setGlobalFret(fretToUse);
    
    setSelectedNotes(prev => {
      // Check if this exact position is already selected
      const existingIndex = prev.findIndex(n => 
        n.stringIndex === stringIndex && 
        !n.isOpen && 
        n.fret === fretToUse
      );
      
      if (existingIndex >= 0) {
        // Remove if already selected
        return prev.filter((_, i) => i !== existingIndex);
      } else {
        // Add new note
        // In slant mode: allow multiple frets per string
        // In normal mode: replace existing fretted note on same string
        const filtered = slantMode 
          ? prev 
          : prev.filter(n => !(n.stringIndex === stringIndex && !n.isOpen));
        return [...filtered, { 
          id: generateNoteId(),
          stringIndex, 
          isOpen: false, 
          fret: fretToUse 
        }].sort((a, b) => a.stringIndex - b.stringIndex);
      }
    });
  }, [slantMode]);

  // Toggle open string selection
  const toggleOpenNote = useCallback((stringIndex) => {
    setSelectedNotes(prev => {
      const existingIndex = prev.findIndex(n => n.stringIndex === stringIndex && n.isOpen);
      if (existingIndex >= 0) {
        return prev.filter((_, i) => i !== existingIndex);
      } else {
        const filtered = prev.filter(n => !(n.stringIndex === stringIndex && n.isOpen));
        return [...filtered, { 
          id: generateNoteId(),
          stringIndex, 
          isOpen: true, 
          fret: 0 
        }].sort((a, b) => a.stringIndex - b.stringIndex);
      }
    });
  }, []);

  // Remove a specific note
  const removeNote = useCallback((noteId) => {
    setSelectedNotes(prev => prev.filter(n => n.id !== noteId));
  }, []);

  // Clear all selected notes
  const clearSelectedNotes = useCallback(() => {
    setSelectedNotes([]);
  }, []);

  // Check if a string position is selected
  // NOTE: In non-slant mode, notes are stored with the fret they were selected at.
  // We highlight a position if that specific stringIndex+fretPos is in selectedNotes.
  const isPositionSelected = useCallback((stringIndex, fretPos, isOpen = false) => {
    if (isOpen) {
      return selectedNotes.some(n => n.stringIndex === stringIndex && n.isOpen);
    }
    // Always check against the actual fret position being rendered
    return selectedNotes.some(n => 
      n.stringIndex === stringIndex && 
      !n.isOpen && 
      n.fret === fretPos
    );
  }, [selectedNotes]);

  // Generate control combinations for grip search
  const generateControlCombinations = useCallback((controls) => {
    const result = [[]];
    for (const ctrl of controls) {
      result.push([ctrl]);
    }
    for (let i = 0; i < controls.length; i++) {
      for (let j = i + 1; j < controls.length; j++) {
        result.push([controls[i], controls[j]]);
      }
    }
    return result;
  }, []);

  // Find chord positions - does NOT reset controls
  // Musical relevance scoring + steel-friendly sorting
  const handleFindChordPositions = useCallback(() => {
    if (!engineCopedant) return;
    
    setSearchingGrips(true);
    
    setTimeout(() => {
      try {
        const controlIds = engineCopedant.controls.map(c => c.id);
        const controlCombos = generateControlCombinations(controlIds);
        
        const allGrips = generateGrips(
          engineCopedant,
          controlCombos,
          {
            fretRange: [chordFinderFretMin, chordFinderFretMax],
            gripSizeRange: [chordFinderStringsMin, chordFinderStringsMax],
            maxFretSpan: 0,
          }
        );
        
        // STRICT family filtering - exact match only
        const filteredGrips = allGrips.filter(grip => {
          if (!grip.chord?.symbol) return false;
          const normalized = normalizeChordFamily(grip.chord.symbol);
          
          // Root filter
          if (chordFinderRoot !== 'any') {
            const normalizedRoot = chordFinderRoot.replace('b', '#');
            if (normalized.root !== normalizedRoot && 
                normalized.root !== chordFinderRoot) {
              return false;
            }
          }
          
          // STRICT family filter - exact match, no cross-family leakage
          if (chordFinderFamily !== 'any') {
            // Must be EXACT family match
            if (normalized.family !== chordFinderFamily) {
              // Only allow extensions within SAME family if enabled
              if (includeExtensions) {
                // Major can include maj7, minor can include min7
                const allowedExtensions = {
                  'major': ['maj7'],
                  'minor': ['min7'],
                };
                const allowed = allowedExtensions[chordFinderFamily] || [];
                if (!allowed.includes(normalized.family)) {
                  return false;
                }
              } else {
                return false;
              }
            }
          }
          return true;
        });
        
        // ========================================
        // NEW: Musical Relevance Scoring + Sorting
        // ========================================
        
        // Step 1: Calculate scores for all grips
        filteredGrips.forEach(grip => {
          // Musical relevance score (higher = more useful for beginners)
          const scoreInfo = calculateMusicalRelevanceScore(grip, chordFinderRoot, includeExtensions);
          grip.musicalScore = scoreInfo.score;
          grip.scoreInfo = scoreInfo;
          
          // Also calculate playability score (lower = easier to play)
          grip.playabilityScore = calculatePlayabilityScore(grip);
          
          // Get label for display
          grip.chordLabel = getChordLabel(grip, scoreInfo);
        });
        
        // Step 2: Sort by musical relevance (primary), then playability (secondary)
        filteredGrips.sort((a, b) => {
          // PRIMARY: Musical relevance score (higher = first)
          const musicalDiff = (b.musicalScore || 0) - (a.musicalScore || 0);
          if (Math.abs(musicalDiff) > 5) {
            return musicalDiff;
          }
          
          // SECONDARY: Playability score (lower = easier = first)
          const playabilityDiff = (a.playabilityScore || 0) - (b.playabilityScore || 0);
          if (Math.abs(playabilityDiff) > 0.5) {
            return playabilityDiff;
          }
          
          // TERTIARY: Fewer skipped strings
          const getSkippedStrings = (grip) => {
            const indices = grip.strings.map(s => s.stringIndex).sort((x, y) => x - y);
            let skipped = 0;
            for (let i = 1; i < indices.length; i++) {
              skipped += (indices[i] - indices[i-1] - 1);
            }
            return skipped;
          };
          const skippedA = getSkippedStrings(a);
          const skippedB = getSkippedStrings(b);
          if (skippedA !== skippedB) return skippedA - skippedB;
          
          // QUATERNARY: Fewer controls
          if (a.activeControls.length !== b.activeControls.length) {
            return a.activeControls.length - b.activeControls.length;
          }
          
          // QUINARY: Lower fret
          if (a.fret !== b.fret) return a.fret - b.fret;
          
          return 0;
        });
        
        setGrips(filteredGrips.slice(0, 100));
        
        if (filteredGrips.length === 0) {
          toast.info('No chord positions found with current settings');
        } else {
          toast.success(`Found ${filteredGrips.length} chord positions`);
        }
      } catch (e) {
        console.error('Error finding chord positions:', e);
        toast.error('Failed to find chord positions');
      } finally {
        setSearchingGrips(false);
      }
    }, 100);
  }, [engineCopedant, chordFinderFretMin, chordFinderFretMax, chordFinderStringsMin, chordFinderStringsMax, chordFinderRoot, chordFinderFamily, includeExtensions, generateControlCombinations]);

  // Apply a grip from results
  const applyGrip = useCallback((grip) => {
    setGlobalFret(grip.fret);
    setActiveControls(grip.activeControls);
    
    // Update selected notes to match grip strings
    const newNotes = grip.strings.map(s => ({
      id: generateNoteId(),
      stringIndex: s.stringIndex,
      isOpen: grip.fret === 0,
      fret: grip.fret
    }));
    setSelectedNotes(newNotes);
  }, []);

  // Format controls for display - using steel player language
  const formatPedalsLevers = useCallback((controls) => {
    if (!controls || controls.length === 0) return 'Open';
    
    // Separate pedals and levers for proper ordering (pedals first, then levers)
    const pedals = controls.filter(c => !isLeverName(c));
    const levers = controls.filter(c => isLeverName(c));
    
    // Combine: pedals first, then levers
    const orderedControls = [...pedals, ...levers];
    return orderedControls.join(' + ');
  }, []);

  // Get displayed grips (limited for free users)
  const displayedGrips = useMemo(() => {
    if (isPro) return grips;
    return grips.slice(0, FREE_GRIP_LIMIT);
  }, [grips, isPro]);

  const hasLockedGrips = !isPro && grips.length > FREE_GRIP_LIMIT;

  // Calculate notes at current global fret with active controls
  const appliedResult = useMemo(() => {
    if (!engineCopedant) return null;
    return applyAtFret(engineCopedant, globalFret, activeControls);
  }, [engineCopedant, globalFret, activeControls]);

  // Calculate open string notes
  const openResult = useMemo(() => {
    if (!engineCopedant) return null;
    return applyAtFret(engineCopedant, 0, activeControls);
  }, [engineCopedant, activeControls]);

  if (loading) {
    return (
      <div className="min-h-screen bg-ss-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-ss-cyan border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // No copedant selected - show selector
  if (!copedantId || !backendCopedant) {
    return (
      <div className="min-h-screen bg-ss-bg">
        <div className="noise-overlay"></div>
        <header className="sticky top-0 z-50 glass-panel">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
              className="text-ss-muted hover:text-ss-text"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight uppercase text-ss-text">
                Explorer
              </h1>
              <p className="text-xs text-ss-muted">Select a copedant to explore</p>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-12">
          <Card className="bg-ss-surface border-ss-border">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-ss-text flex items-center gap-2">
                <Music className="w-6 h-6 text-ss-cyan" />
                Choose a Copedant
              </CardTitle>
            </CardHeader>
            <CardContent>
              {allCopedants.length === 0 ? (
                <div className="text-center py-8">
                  <Music className="w-12 h-12 text-ss-muted mx-auto mb-4" />
                  <p className="text-ss-muted mb-4">No copedants available. Create one first.</p>
                  <Button
                    onClick={() => navigate('/copedants/new')}
                    className="bg-ss-cyan hover:bg-ss-cyan/90 text-black"
                  >
                    Create Copedant
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {allCopedants.map(cop => (
                    <div
                      key={cop.copedant_id}
                      onClick={() => handleSelectCopedant(cop.copedant_id)}
                      className="p-4 bg-ss-bg border border-ss-border rounded-lg hover:border-ss-cyan/50 cursor-pointer transition-colors"
                      data-testid={`select-copedant-${cop.copedant_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-ss-text flex items-center gap-2">
                            {cop.name}
                            {cop.is_sample && (
                              <span className="text-[10px] px-2 py-0.5 bg-ss-red/20 text-ss-red rounded-full">Sample</span>
                            )}
                          </h3>
                          <p className="text-sm text-ss-muted">
                            {cop.strings?.length || 0} strings • {cop.controls?.length || 0} controls
                          </p>
                        </div>
                        <Button size="sm" className="bg-ss-cyan hover:bg-ss-cyan/90 text-black">
                          Explore
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ss-bg flex flex-col">
      <div className="noise-overlay"></div>

      {/* Compact Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
              className="text-ss-muted hover:text-ss-text p-2"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <Select value={copedantId} onValueChange={handleSelectCopedant}>
              <SelectTrigger className="w-52 bg-ss-surface border-ss-border text-ss-text text-sm h-9">
                <SelectValue placeholder="Select copedant" />
              </SelectTrigger>
              <SelectContent className="bg-ss-surface border-ss-border">
                {allCopedants.map(cop => (
                  <SelectItem key={cop.copedant_id} value={cop.copedant_id} className="text-ss-text text-sm">
                    {cop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-4">
            {/* Slant Mode Toggle */}
            <div className="flex items-center gap-2">
              <Label className="text-xs text-ss-muted">Slant</Label>
              <Switch
                checked={slantMode}
                onCheckedChange={setSlantMode}
                className="scale-75"
              />
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowChordFinder(!showChordFinder)}
              className={`text-ss-muted hover:text-ss-text ${showChordFinder ? 'bg-ss-surface-highlight' : ''}`}
            >
              <Search className="w-4 h-4 mr-2" />
              Chord Finder
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 py-4 w-full">
        {/* Row 1: Controls + Chord Analysis + Selected Notes */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          {/* Pedals & Levers */}
          <Card className="bg-ss-surface border-ss-border">
            <CardContent className="py-4">
              {engineCopedant && engineCopedant.controls.length === 0 ? (
                <div className="text-center py-2">
                  <p className="text-ss-cyan text-sm font-semibold">Lap Steel</p>
                  <p className="text-ss-muted text-xs mt-1">No pedals or knee levers</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {groupedControls.pedals.length > 0 && (
                    <div>
                      <p className="text-[10px] text-ss-muted mb-2 uppercase tracking-wider font-semibold">Pedals</p>
                      <div className="flex flex-wrap gap-1.5">
                        {groupedControls.pedals.map(control => (
                          <button
                            key={control.id}
                            onClick={() => toggleControl(control.id)}
                            data-testid={`control-btn-${control.id}`}
                            className={`pedal-button-sm ${activeControls.includes(control.id) ? 'pressed' : ''}`}
                          >
                            {control.id}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {groupedControls.levers.length > 0 && (
                    <div>
                      <p className="text-[10px] text-ss-muted mb-2 uppercase tracking-wider font-semibold">Knee Levers</p>
                      <div className="flex flex-wrap gap-1.5">
                        {groupedControls.levers.map(control => (
                          <button
                            key={control.id}
                            onClick={() => toggleControl(control.id)}
                            data-testid={`control-btn-${control.id}`}
                            className={`pedal-button-sm lever ${activeControls.includes(control.id) ? 'pressed' : ''}`}
                          >
                            {control.id}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Chord Analysis */}
          <Card className="bg-ss-surface border-ss-border">
            <CardContent className="py-4">
              <div className="text-center">
                {(() => {
                  const formatted = formatChordDisplay(chordInfo);
                  return (
                    <>
                      <p className="text-5xl font-heading font-black text-ss-cyan mb-2" data-testid="chord-name">
                        {formatted.displayName}
                      </p>
                      {chordInfo ? (
                        <div className="text-xs text-ss-muted space-y-1">
                          <p>
                            <span className="text-ss-cyan">{chordInfo.root}</span>
                            {' • '}
                            <span className="text-ss-muted">{chordInfo.quality || 'major'}</span>
                          </p>
                          {/* Show bass/inversion details if available */}
                          {formatted.details && (
                            <p className="text-ss-gold/80">
                              {formatted.details.inversion && <span>{formatted.details.inversion} • </span>}
                              Bass: {formatted.details.bass}
                            </p>
                          )}
                          {activeControls.length > 0 && (
                            <p className="text-ss-red">
                              Pedals/Levers: {formatPedalsLevers(activeControls)}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-ss-muted">Click any note to start building a chord.</p>
                      )}
                    </>
                  );
                })()}
              </div>
            </CardContent>
          </Card>

          {/* Selected Notes Panel */}
          <Card className="bg-ss-surface border-ss-border">
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-ss-muted uppercase tracking-wider font-semibold">Selected Notes</p>
                {selectedNotes.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSelectedNotes}
                    className="h-6 px-2 text-ss-muted hover:text-ss-red"
                    data-testid="clear-notes-btn"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
              {computedSelectedNotes.length === 0 ? (
                <p className="text-sm text-ss-muted">Click fretboard to add notes.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {computedSelectedNotes.map((note) => (
                    <div
                      key={note.id}
                      className="group flex items-center gap-1 px-2 py-1 bg-ss-bg border border-ss-border rounded hover:border-ss-red/50 transition-colors"
                      data-testid={`selected-note-${note.id}`}
                    >
                      <span className="font-mono font-bold text-ss-cyan text-sm">
                        {note.noteName?.replace(/\d+$/, '')}
                      </span>
                      <span className="text-[9px] text-ss-muted">
                        {note.isOpen ? 'open' : `f${note.fret}`}
                      </span>
                      {note.activeControlsOnString?.length > 0 && (
                        <span className="text-[8px] text-ss-red">
                          ({note.activeControlsOnString.join(',')})
                        </span>
                      )}
                      <button
                        onClick={() => removeNote(note.id)}
                        className="ml-1 text-ss-muted hover:text-ss-red opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Row 2: Clickable Fret Ruler + Fretboard */}
        <Card className="bg-ss-surface border-ss-border mb-4 overflow-hidden">
          <CardContent className="p-0">
            {/* Helper text */}
            <div className="px-4 py-2 bg-ss-bg/50 border-b border-ss-border flex items-center justify-between">
              <p className="text-xs text-ss-muted">
                Click any note to select it. Toggle pedals/levers to explore voicings.
                {slantMode && <span className="text-ss-gold ml-2">Slant Mode: select different frets per string.</span>}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowExtendedFrets(!showExtendedFrets)}
                className="text-xs text-ss-muted hover:text-ss-text"
              >
                {showExtendedFrets ? 'Show 0-12' : 'Show 0-24'}
              </Button>
            </div>

            {/* Clickable Fret Ruler */}
            <div className="px-4 py-2 border-b border-ss-border bg-ss-bg/30">
              <div className="flex" style={{ paddingLeft: '90px' }}>
                {Array.from({ length: maxVisibleFret + 1 }).map((_, fretNum) => (
                  <button
                    key={fretNum}
                    onClick={() => setGlobalFret(fretNum)}
                    data-testid={`fret-btn-${fretNum}`}
                    className={`flex-1 py-1 text-xs font-mono transition-all ${
                      globalFret === fretNum
                        ? 'bg-ss-cyan text-black font-bold'
                        : 'text-ss-muted hover:bg-ss-surface-highlight hover:text-ss-text'
                    } ${[3, 5, 7, 9, 12, 15, 17, 19, 21, 24].includes(fretNum) ? 'font-semibold' : ''}`}
                  >
                    {fretNum}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Fretboard */}
            <div className="fretboard relative overflow-x-auto" style={{ minHeight: `${(engineCopedant?.openTuning.length || 10) * 34 + 40}px` }}>
              {/* Fret markers - improved visibility */}
              <div className="absolute inset-0 flex" style={{ paddingLeft: '90px' }}>
                {Array.from({ length: maxVisibleFret + 1 }).map((_, i) => {
                  const isKeyFret = [3, 5, 7, 9, 12, 15, 17, 19, 21, 24].includes(i);
                  const is12thFret = i === 12;
                  const isCurrentFret = globalFret === i && !slantMode;
                  
                  return (
                    <div
                      key={i}
                      className={`flex-1 relative ${is12thFret ? 'fret-12' : ''}`}
                      style={{ 
                        borderRight: isKeyFret 
                          ? `${is12thFret ? '2px' : '1px'} solid rgba(255, 255, 255, ${is12thFret ? '0.35' : '0.25'})` 
                          : '1px solid rgba(255, 255, 255, 0.08)',
                        background: isCurrentFret 
                          ? 'rgba(0, 229, 255, 0.08)' 
                          : isKeyFret 
                            ? `rgba(255, 255, 255, ${is12thFret ? '0.04' : '0.025'})` 
                            : 'transparent'
                      }}
                    >
                      {/* Key fret position markers - double dots at 12 */}
                      {isKeyFret && (
                        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${is12thFret ? 'flex gap-2' : ''}`}>
                          <div className={`rounded-full ${is12thFret ? 'w-2.5 h-2.5 bg-white/40' : 'w-2 h-2 bg-white/25'}`}></div>
                          {is12thFret && <div className="w-2.5 h-2.5 rounded-full bg-white/40"></div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Strings - NO row-level highlighting */}
              <div className="relative z-10 py-3" style={{ paddingLeft: '90px' }}>
                {appliedResult?.strings.map((stringState, idx) => {
                  const openNote = openResult?.strings[idx]?.noteName || '';
                  const isOpenSelected = isPositionSelected(idx, 0, true);

                  return (
                    <div
                      key={idx}
                      className="relative h-8 flex items-center"
                      style={{ background: 'transparent' }}
                    >
                      {/* String number */}
                      <div className="absolute left-0 -ml-[90px] w-6 h-6 flex items-center justify-center">
                        <span className="text-[10px] font-mono text-ss-muted">
                          {idx + 1}
                        </span>
                      </div>

                      {/* Open note label - clickable, highlight ONLY this label (gold) */}
                      <div 
                        className={`absolute left-0 -ml-16 w-10 h-6 flex items-center justify-center cursor-pointer rounded transition-all ${
                          isOpenSelected 
                            ? 'bg-amber-500/25 border border-amber-400 shadow-sm shadow-amber-400/30' 
                            : 'hover:bg-white/5 border border-transparent hover:border-gray-700'
                        }`}
                        onClick={() => toggleOpenNote(idx)}
                        title={`Open: ${openNote?.replace(/\d+$/, '')}`}
                        data-testid={`open-string-${idx}`}
                      >
                        <span className={`text-xs font-mono font-bold ${isOpenSelected ? 'text-amber-400' : 'text-gray-500'}`}>
                          {openNote?.replace(/\d+$/, '')}
                        </span>
                      </div>

                      {/* String line - STATIC appearance, NEVER highlights */}
                      <div
                        className="w-full absolute inset-0 top-1/2 -translate-y-1/2 pointer-events-none"
                        style={{ 
                          height: `${1 + ((engineCopedant?.openTuning.length || 10) - idx) * 0.08}px`,
                          background: 'linear-gradient(to right, rgba(150,150,150,0.25), rgba(180,180,180,0.35), rgba(150,150,150,0.25))'
                        }}
                      ></div>

                      {/* Note markers for each visible fret position - ONLY these pills highlight */}
                      {Array.from({ length: maxVisibleFret + 1 }).map((_, fretNum) => {
                        const isSelected = isPositionSelected(idx, fretNum, false);
                        const noteAtFret = getNoteAtPosition(idx, fretNum, activeControls);
                        const isGlobalFret = fretNum === globalFret && !slantMode;
                        
                        return (
                          <div
                            key={fretNum}
                            className={`note-marker cursor-pointer ${isSelected ? 'selected' : ''} ${isGlobalFret && !isSelected ? 'current-fret' : ''}`}
                            style={{
                              left: `calc(${(fretNum / maxVisibleFret) * 100}% + ${fretNum === 0 ? 6 : 0}px)`,
                              top: '50%',
                              transform: isSelected ? 'translate(-50%, -50%) scale(1.3)' : 'translate(-50%, -50%)',
                              fontSize: '10px',
                              width: '22px',
                              height: '22px'
                            }}
                            onClick={() => toggleFrettedNote(idx, fretNum)}
                            data-testid={`note-marker-${idx}-${fretNum}`}
                          >
                            {noteAtFret?.noteName?.replace(/\d+$/, '') || '?'}
                          </div>
                        );
                      })}

                      {/* Active controls indicator on string */}
                      {stringState.activeControls?.length > 0 && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-0.5 z-30">
                          {stringState.activeControls.map(ctrl => (
                            <span key={ctrl} className="text-[7px] px-1 py-0.5 bg-ss-red/30 text-ss-red rounded">
                              {ctrl}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Row 3: Chord Finder (Collapsible) */}
        {showChordFinder && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Chord Finder Panel */}
            <Card className="bg-ss-surface border-ss-border">
              <CardHeader className="py-3">
                <CardTitle className="font-heading text-sm text-ss-text flex items-center gap-2">
                  <Target className="w-4 h-4 text-ss-gold" />
                  Chord Finder
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-ss-muted text-xs">Root</Label>
                    <Select value={chordFinderRoot} onValueChange={setChordFinderRoot}>
                      <SelectTrigger className="mt-1 bg-ss-bg border-ss-border text-ss-text h-8 text-sm" data-testid="chord-root-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-ss-surface border-ss-border">
                        <SelectItem value="any" className="text-ss-text">Any</SelectItem>
                        {ROOT_NOTES.map(note => (
                          <SelectItem key={note} value={note} className="text-ss-text">{note}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-ss-muted text-xs">Family</Label>
                    <Select value={chordFinderFamily} onValueChange={setChordFinderFamily}>
                      <SelectTrigger className="mt-1 bg-ss-bg border-ss-border text-ss-text h-8 text-sm" data-testid="chord-family-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-ss-surface border-ss-border">
                        <SelectItem value="any" className="text-ss-text">Any</SelectItem>
                        {CHORD_FAMILIES.map(family => (
                          <SelectItem key={family.value} value={family.value} className="text-ss-text">{family.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <Label className="text-ss-muted text-xs">Include Extensions</Label>
                  <Switch
                    checked={includeExtensions}
                    onCheckedChange={setIncludeExtensions}
                    className="scale-75"
                  />
                </div>

                <Collapsible open={showAdvancedSettings} onOpenChange={setShowAdvancedSettings}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full justify-between text-ss-muted hover:text-ss-text h-7 text-xs">
                      <span className="flex items-center gap-1">
                        <Settings className="w-3 h-3" />
                        Advanced
                      </span>
                      <ChevronDown className={`w-3 h-3 transition-transform ${showAdvancedSettings ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-2 pt-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-ss-muted text-[10px]">Min Fret</Label>
                        <Input
                          type="number"
                          value={chordFinderFretMin}
                          onChange={(e) => setChordFinderFretMin(Math.max(0, parseInt(e.target.value) || 0))}
                          min={0}
                          max={24}
                          className="mt-1 bg-ss-bg border-ss-border text-ss-text h-7 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-ss-muted text-[10px]">Max Fret</Label>
                        <Input
                          type="number"
                          value={chordFinderFretMax}
                          onChange={(e) => setChordFinderFretMax(Math.min(24, parseInt(e.target.value) || 24))}
                          min={0}
                          max={24}
                          className="mt-1 bg-ss-bg border-ss-border text-ss-text h-7 text-xs"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-ss-muted text-[10px]">Min Strings</Label>
                        <Input
                          type="number"
                          value={chordFinderStringsMin}
                          onChange={(e) => setChordFinderStringsMin(Math.max(2, parseInt(e.target.value) || 3))}
                          min={2}
                          max={engineCopedant?.openTuning.length || 10}
                          className="mt-1 bg-ss-bg border-ss-border text-ss-text h-7 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-ss-muted text-[10px]">Max Strings</Label>
                        <Input
                          type="number"
                          value={chordFinderStringsMax}
                          onChange={(e) => setChordFinderStringsMax(Math.min(engineCopedant?.openTuning.length || 10, parseInt(e.target.value) || 6))}
                          min={2}
                          max={engineCopedant?.openTuning.length || 10}
                          className="mt-1 bg-ss-bg border-ss-border text-ss-text h-7 text-xs"
                        />
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <Button
                  onClick={handleFindChordPositions}
                  disabled={searchingGrips}
                  data-testid="find-chord-positions-btn"
                  className="w-full bg-ss-gold hover:bg-ss-gold/90 text-black font-semibold h-9"
                >
                  {searchingGrips ? (
                    <>
                      <div className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-3 h-3 mr-2" />
                      Find Chord Grips
                    </>
                  )}
                </Button>

                {!isPro && (
                  <p className="text-[10px] text-ss-muted text-center">
                    Free: Top {FREE_GRIP_LIMIT} results
                  </p>
                )}
                {(isForcePro() || isAdminEmail) && !user?.is_pro && (
                  <p className="text-[10px] text-ss-gold text-center">
                    ✓ Pro features unlocked (preview mode)
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Chord Results - spans 2 columns */}
            <Card className="bg-ss-surface border-ss-border lg:col-span-2">
              <CardHeader className="py-3">
                <CardTitle className="font-heading text-sm text-ss-text flex items-center gap-2">
                  <Info className="w-4 h-4 text-ss-gold" />
                  Chord Results
                  {grips.length > 0 && (
                    <span className="ml-auto text-xs font-normal text-ss-muted">
                      ({isPro ? grips.length : `${Math.min(grips.length, FREE_GRIP_LIMIT)} of ${grips.length}`})
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                  {grips.length === 0 ? (
                    <div className="col-span-2 text-center py-6">
                      <Search className="w-8 h-8 text-ss-muted mx-auto mb-2 opacity-50" />
                      <p className="text-ss-muted text-sm">Search for chord positions above</p>
                    </div>
                  ) : (
                    <>
                      {displayedGrips.map((grip, idx) => {
                        const difficulty = getDifficultyLabel(grip.playabilityScore || 0);
                        const reason = getDifficultyReason(grip);
                        const chordLabel = grip.chordLabel;
                        
                        return (
                          <div
                            key={idx}
                            className="p-3 bg-ss-bg border border-ss-border rounded-lg hover:border-ss-cyan/30 transition-colors cursor-pointer"
                            data-testid={`grip-${idx}`}
                            onClick={() => applyGrip(grip)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="font-heading font-bold text-ss-cyan">
                                  {grip.chord?.symbol || 'Unknown'}
                                </span>
                                {/* Musical relevance label badge */}
                                {chordLabel && (
                                  <span 
                                    className={`text-[9px] px-1.5 py-0.5 rounded border ${chordLabel.color}`}
                                    data-testid={`chord-label-${idx}`}
                                  >
                                    {chordLabel.text}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span 
                                  className={`text-[10px] px-1.5 py-0.5 rounded ${difficulty.color} bg-white/5`}
                                  title={reason}
                                >
                                  {difficulty.label}
                                </span>
                                <span className="text-xs px-2 py-0.5 bg-ss-surface-highlight rounded text-ss-muted">
                                  Fret {grip.fret}
                                </span>
                              </div>
                            </div>
                            <div className="space-y-1 text-[11px]">
                              <div className="flex items-center gap-2">
                                <span className="text-ss-muted">Pedals/Levers:</span>
                                <span className={`font-semibold ${grip.activeControls.length > 0 ? 'text-ss-red' : 'text-ss-cyan'}`}>
                                  {formatPedalsLevers(grip.activeControls)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-ss-muted">Strings:</span>
                                <span className="text-ss-text font-mono">
                                  {grip.strings.map(s => s.stringIndex + 1).join('-')}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-ss-muted">Notes:</span>
                                {grip.strings.map(s => (
                                  <span key={s.stringIndex} className="text-ss-cyan font-mono">
                                    {s.noteName?.replace(/\d+$/, '')}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {hasLockedGrips && (
                        <div className="col-span-2 p-4 bg-ss-bg/50 border border-ss-gold/30 rounded-lg text-center">
                          <Lock className="w-6 h-6 text-ss-gold mx-auto mb-2" />
                          <p className="text-ss-text text-sm font-semibold mb-2">
                            +{grips.length - FREE_GRIP_LIMIT} more positions
                          </p>
                          <Button
                            onClick={() => navigate('/upgrade')}
                            size="sm"
                            className="bg-ss-gold hover:bg-ss-gold/90 text-black"
                          >
                            <Zap className="w-3 h-3 mr-1" />
                            Upgrade to Pro
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default ExplorerPage;
